import { deepEqual } from "../kl-utils";
import { getAnalyticsOptions } from "./analytics";

const discovered = [];

export function pushToDataLayer(dataLayer: any[], eventData: any) {
    if (
        dataLayer.length > 0 &&
        deepEqual(eventData, dataLayer[dataLayer.length - 1], {
            onlyKeys: ["gtm.uniqueEventId"],
        })
    )
        return;
    dataLayer.push(eventData);
}

export default class GoogleTagManager {
    _dataLayer;
    _dataLayerName;
    _trackIndex;
    _pageViewEventName;

    /**
     * @param {*} dataLayer - The google tag manager dataLayer
     * @param {string} dataLayerName - The name of the dataLayer
     * @param {number} trackIndex - The trackIndex the tracker should start with
     * @param {string} pageViewEventName - The name used for the page view event
     */
    constructor(dataLayer, dataLayerName, trackIndex = -1, pageViewEventName = "ps.PageView") {
        this._dataLayer = dataLayer;
        this._dataLayerName = dataLayerName;
        this._trackIndex = trackIndex;
        this._pageViewEventName = pageViewEventName;
    }

    get trackIndex() {
        return this._trackIndex;
    }

    /**
     * track an url or event
     * @param {string} type - The type of track
     * @param {*} payload - The tracking payload
     * @param {number} trackIndex - The index of the track event
     * @returns {Promise} - resolves when it is ok to continue tracking
     */
    track(type, {category, ...rest}, trackIndex) {
        this._trackIndex = trackIndex;
        if (type === 'event') {
            return this._trackEvent(
                category, 
                rest
            );
        } else if(type === 'page') {
            return this._trackPage(rest.url, rest.title);
        }
    }
    _pushToDataLayer(data: any) {
        pushToDataLayer(this._dataLayer, data);
    }

    /**
     * track an url
     * @param {string} url - The url of the page
     * @param {string} title - The title of the page
     * @returns {Promise} - resolves when it is ok to continue tracking
     */
    _trackPage(url, title) {
        let freshExtras = {};
        try {
            //because fresh view isn't capable of having a proper GTM config
            freshExtras = {
                pageUrl: window.location.toString(),
                pagePath: url,
                pageTitle: title
            }
        } catch (e) {}

        this._pushToDataLayer({
            event: this._pageViewEventName,
            url,
            title,
            ...freshExtras
        });
        return Promise.resolve(true);
    }

    /**
     * track an event
     * @param {*} payload - the event payload
     * @returns {Promise} - resolves when it is ok to continue tracking
     */
    _trackEvent(category, payload) {
        this._pushToDataLayer({
            event: category,
            payload,
        });
        return Promise.resolve(true);
    }

    /**
     * destroy the tracker instance and clean up if necessary
     */
    destroy() {
        discovered.splice(discovered.indexOf(this._dataLayerName), 1);
    }

    /**
     * repeat calls to this function will not discover the same tracker again
     * @param {number} trackIndex - The trackIndex the tracker should start with
     * @returns - A promise resolving an array of wrapper instances for discovered tracker
     */
    static discover(trackIndex = -1) {
        return new Promise((resolve, reject) => {
            if (window.dataLayer && !discovered.includes('dataLayer')) {
                discovered.push('dataLayer');
                resolve([new GoogleTagManager(window.dataLayer, 'dataLayer', trackIndex, getAnalyticsOptions().overrideGTMPageViewEventName)]);
            } else {
                resolve([])
            }
        });
    }

    /**
     * initialize general purpose things like the tracker's library js
     */
    static init() {
        return Promise.resolve();
    }

    /**
     * @param {*} options - The tracker options 
     * @returns - A promise resolving the wrapper instance
     */
    static create({code, dataLayer = 'dataLayer', pageViewEventName = "ps.PageView"}) {
        return new Promise((resolve, reject) => {
            if (
                ~discovered.indexOf(dataLayer) &&
                window[dataLayer] &&
                window[dataLayer].length &&
                window[dataLayer].find(val => !!val["gtm.start"])
            ) {
                resolve(null);
                return;
            }
            
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script', dataLayer, code);

            window[dataLayer] = window[dataLayer] || [];
            discovered.push(dataLayer);
            resolve(new GoogleTagManager(window[dataLayer], dataLayer, undefined, pageViewEventName));
        })
    }
}


// WEBPACK FOOTER //
// ./lib/analytics/tracker-googletagmanager.js